import { Component, DestroyRef, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { combineLatest, filter, map, startWith } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@auth0/auth0-angular';
import { SessionService } from '../../services/session/session.service';
import { navItems } from './_nav';
import { RoleINavData } from './role-nav.model';
import { PermissionsService } from '../../core/permissions/permissions.service';

@Component({
    templateUrl: './default-layout.component.html',
    styleUrl: './default-layout.component.scss',
})
export class DefaultLayoutComponent implements OnInit {
    navItems = signal<RoleINavData[]>([]);
    fullScreen = signal(false);
    isRootAdmin = toSignal(this.sessionService.originalRole$.pipe(map(r => r === 'ROOT_ADMIN')), { initialValue: false });
    rootBannerDismissed = signal(false);

    constructor(
        public auth: AuthService,
        private router: Router,
        private route: ActivatedRoute,
        private destroyRef: DestroyRef,
        private sessionService: SessionService,
        private permissions: PermissionsService,
    ) {
        // Rebuild nav whenever role OR route context (org/account) changes.
        combineLatest([
            this.sessionService.assumedRole$,
            this.route.params.pipe(map(p => ({
                orgId:     (p['org_id']      as string) ?? null,
                accountId: (p['account_id']  as string) ?? null,
            }))),
        ])
            .pipe(takeUntilDestroyed())
            .subscribe(([, { orgId, accountId }]) => {
                this.navItems.set(
                    structuredClone(navItems)
                        .filter(item => this.filterNavItem(item) && this.isItemReachable(item, orgId, accountId))
                        .map(item => {
                            const out = { ...item, url: this.prefixUrl(item.url, orgId, accountId, item.scope) };
                            if (out.children) {
                                out.children = out.children
                                    .filter(child => this.filterNavItem(child) && this.isItemReachable(child, orgId, accountId))
                                    .map(child => ({ ...child, url: this.prefixUrl(child.url, orgId, accountId, child.scope) }));
                            }
                            return out;
                        }),
                );
            });
    }

    ngOnInit() {
        this.setFullScreen();
    }

    /**
     * Prefix nav URLs based on scope:
     * - 'org'     → /org/<orgId>/<url>
     * - 'account' → /account/<accountId>/<url>
     * - 'global'  → keep as-is
     */
    private prefixUrl(
        url: string | unknown[] | undefined,
        orgId: string | null,
        accountId: string | null,
        scope?: 'org' | 'account' | 'global',
    ): string | unknown[] | undefined {
        if (!url || typeof url !== 'string') return url;
        if (scope === 'org' && orgId)         return `/orgs/${orgId}/${url.replace(/^\//, '')}`;
        if (scope === 'account' && accountId) return `/accounts/${accountId}/${url.replace(/^\//, '')}`;
        return url;
    }

    private filterNavItem(item: RoleINavData): boolean {
        if (!item.subject) return true;
        return this.permissions.can('read', item.subject);
    }

    /**
     * Show items only in their matching context:
     * - 'org'     → only when org_id is in the URL
     * - 'account' → only when account_id is in the URL
     * - 'global'  → only when neither org nor account context is active (e.g. /accounts)
     */
    private isItemReachable(item: RoleINavData, orgId: string | null, accountId: string | null): boolean {
        if (!item.scope)              return true;
        if (item.scope === 'org')     return !!orgId;
        if (item.scope === 'account') return !!accountId;
        if (item.scope === 'global')  return !orgId && !accountId;
        return false;
    }

    private setFullScreen() {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                startWith(this.router),
                map((event) => event as NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((event) => {
                const url = event.url.split(';')[0];
                this.fullScreen.set(url.endsWith('/map'));
            });
    }
}
